import { Stack, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import DefentSheet from "@/features/HealthScore/components/DefentSheet";
import IconLoader from "@/features/Common/components/IconLoader";
import { convertPxToRem } from "@/utils";
import theme from "@/theme";

interface SamsungGuidanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Renders a translation string containing `<strong>` tags with bold Typography spans.
 */
function RichText({ text }: { text: string }) {
  return (
    <>
      {String(text)
        .split("<strong>")
        .map((part: string, i: number) => {
          if (i === 0) return part;
          const [boldText, ...rest] = part.split("</strong>");
          return (
            <span key={i}>
              <Typography component="span" fontWeight={600} fontSize="inherit">
                {boldText}
              </Typography>
              {rest.join("</strong>")}
            </span>
          );
        })}
    </>
  );
}

const steps = [
  "health.samsung.consentStep",
  "health.samsung.cloudSyncStep",
  "health.samsung.permissionStep",
  "health.samsung.restartStep",
] as const;

export default function SamsungGuidanceModal({
  isOpen,
  onClose,
}: SamsungGuidanceModalProps) {
  const { t } = useTranslation();

  return (
    <DefentSheet
      expanded={isOpen}
      onClose={onClose}
      onExpandedChange={(expanded) => {
        if (!expanded) onClose();
      }}
    >
      <Stack pt={4} mb={0} pb={0}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <IconLoader
            icon="AndroidIcon"
            color="primary"
            sx={{ fontSize: convertPxToRem(50) }}
          />
          <Stack spacing={1}>
            <Typography variant="h3" fontWeight={700} component="h2" px={2}>
              {t("health.samsung.guidanceTitle")}
            </Typography>
            <Typography variant="body1" color={theme.palette.neutral[700]}>
              {t("health.samsung.guidanceSubtitle")}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          component="ol"
          spacing={1.5}
          mt={3}
          sx={{
            textAlign: "left",
            paddingInlineStart: convertPxToRem(24),
          }}
        >
          {steps.map((stepKey) => (
            <Typography
              key={stepKey}
              variant="body2"
              component="li"
              color={theme.palette.neutral[900]}
              sx={{ lineHeight: 1.6 }}
            >
              <RichText text={String(t(stepKey as any))} />
            </Typography>
          ))}
        </Stack>

        <Box
          mt={3}
          sx={{
            backgroundColor: theme.palette.secondary[100],
            borderRadius: 2,
            p: 2,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            textAlign: "left",
          }}
          data-testid="samsung-guidance-watch-note"
        >
          <IconLoader
            icon="FlagIcon"
            sx={{
              fontSize: convertPxToRem(20),
              color: theme.palette.secondary[900],
              flexShrink: 0,
              mt: 0.25,
            }}
          />
          <Typography variant="body2" color={theme.palette.neutral[900]}>
            {t("health.samsung.watchNote")}
          </Typography>
        </Box>

        <Box
          mt={2}
          sx={{
            backgroundColor: theme.palette.neutral[100],
            borderRadius: 2,
            p: 2,
            textAlign: "left",
          }}
          data-testid="samsung-guidance-troubleshoot"
        >
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color={theme.palette.neutral[900]}
            mb={0.5}
          >
            {t("health.samsung.stillNotWorking")}
          </Typography>
          <Typography variant="body2" color={theme.palette.neutral[700]}>
            {t("health.samsung.resetInstructions")}
          </Typography>
        </Box>
      </Stack>
    </DefentSheet>
  );
}
